import { createReducer } from "@reduxjs/toolkit";
import type { AnyAction } from "redux";

import {
    convertReaction,
    convertMultiStepReaction,
    IConvertReactionResponse,
    IConvertMultiStepReactionResponse,
    removeReaction,
    removeMultiStepReaction,
    submitReactionsEditModal,
    submitReactionsLoadModal,
    submitMultiStepReactionsEditModal,
} from "../../actions/reactions";
import { IReaction, IMultiStepReaction } from "../reactions";

export type IUserReactionsPersistentState = {
    reactions: IReaction[];
    multiStepReactions: IMultiStepReaction[];
};

export type IReactionsPersistentState = {
    byUser: Record<number, IUserReactionsPersistentState>;
};

type UserScopedPayloadAction<Payload> = {
    payload: Payload;
    meta?: {
        userId?: number;
    };
};

type UserScopedAction = {
    type?: unknown;
    meta?: {
        userId?: unknown;
    };
};

export const getDefaultUserState = (): IUserReactionsPersistentState => ({
    reactions: [],
    multiStepReactions: [],
});

export const getDefaultState = (): IReactionsPersistentState => ({
    byUser: {},
});

const getUserState = (
    state: IReactionsPersistentState,
    action: UserScopedAction
): IUserReactionsPersistentState | undefined => {
    const userId = action.meta?.userId;
    if (typeof userId !== "number") return undefined;
    if (!state.byUser[userId]) {
        state.byUser[userId] = getDefaultUserState();
    }
    return state.byUser[userId];
};

export const reactions = createReducer(getDefaultState(), (builder) => {
    builder.addCase(
        submitReactionsEditModal,
        (state, action: UserScopedPayloadAction<IReaction>) => {
            const userState = getUserState(state, action);
            if (!userState) return;
            const existingIndex = userState.reactions.findIndex(
                (r) => r.ui_id === action.payload.ui_id
            );
            if (existingIndex >= 0) {
                userState.reactions[existingIndex] = action.payload;
            } else {
                userState.reactions.push(action.payload);
            }
        }
    );
    builder.addCase(
        removeReaction,
        (state, action: UserScopedPayloadAction<IReaction>) => {
            const userState = getUserState(state, action);
            if (!userState) return;
            const existingIndex = userState.reactions.findIndex(
                (r) => r.ui_id === action.payload.ui_id
            );
            if (existingIndex >= 0) {
                userState.reactions.splice(existingIndex, 1);
            }
        }
    );
    builder.addCase(
        submitReactionsLoadModal,
        (state, action: UserScopedPayloadAction<IReaction>) => {
            const userState = getUserState(state, action);
            if (!userState) return;
            const existingIndex = userState.reactions.findIndex(
                (r) => r.ui_id === action.payload.ui_id
            );
            if (existingIndex >= 0) {
                userState.reactions[existingIndex] = action.payload;
            } else {
                userState.reactions.push(action.payload);
            }
        }
    );
    builder.addCase(convertReaction.success, (state, action: AnyAction) => {
        const userState = getUserState(state, action);
        if (!userState) return;
        const name = action.meta?.reactionName as string;
        const response = action.payload as IConvertReactionResponse;

        if (!response.reaction || !response.reaction.ui_id) {
            return;
        }

        let reaction = response.reaction;
        if (name) {
            reaction = {
                ...reaction,
                name,
            };
        }

        const existingIndex = userState.reactions.findIndex(
            (r) => r.ui_id === reaction.ui_id
        );
        if (existingIndex >= 0) {
            userState.reactions[existingIndex] = reaction;
        } else {
            userState.reactions.push(reaction);
        }
    });
    builder.addCase(
        submitMultiStepReactionsEditModal,
        (state, action: UserScopedPayloadAction<IMultiStepReaction>) => {
            const userState = getUserState(state, action);
            if (!userState) return;
            const existingIndex = userState.multiStepReactions.findIndex(
                (r) => r.ui_id === action.payload.ui_id
            );
            if (existingIndex >= 0) {
                userState.multiStepReactions[existingIndex] = action.payload;
            } else {
                userState.multiStepReactions.push(action.payload);
            }
        }
    );
    builder.addCase(
        removeMultiStepReaction,
        (state, action: UserScopedPayloadAction<IMultiStepReaction>) => {
            const userState = getUserState(state, action);
            if (!userState) return;
            const existingIndex = userState.multiStepReactions.findIndex(
                (r) => r.ui_id === action.payload.ui_id
            );
            if (existingIndex >= 0) {
                userState.multiStepReactions.splice(existingIndex, 1);
            }
        }
    );
    builder.addCase(
        convertMultiStepReaction.success,
        (state, action: AnyAction) => {
            const userState = getUserState(state, action);
            if (!userState) return;
            const name = action.meta?.reactionName as string;
            const description = action.meta?.reactionDescription as string;
            const response =
                action.payload as IConvertMultiStepReactionResponse;

            if (!response.reactionRoute || !response.reactionRoute.ui_id) {
                return;
            }

            let multiStepReaction = response.reactionRoute;
            if (name || description) {
                multiStepReaction = {
                    ...multiStepReaction,
                    name:
                        name ||
                        multiStepReaction.name ||
                        "Imported Multi-Step Reaction",
                    description: description || multiStepReaction.description,
                };
            }

            const existingIndex = userState.multiStepReactions.findIndex(
                (r) => r.ui_id === multiStepReaction.ui_id
            );
            if (existingIndex >= 0) {
                userState.multiStepReactions[existingIndex] = multiStepReaction;
            } else {
                userState.multiStepReactions.push(multiStepReaction);
            }
        }
    );
});
